import { memo, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, FormHelp, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditWebApiHandler_pets } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { postingConstraints } from '@zg-rentals/zillow-rental-manager/utils';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import useForm from '../../../hooks/useForm';
import { useIsInputFormDisabled } from '../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import propertyUtils from '../../../utils/propertyUtils';

import { useAuth } from '../../AuthProvider';

import type { GetUserPropertyDetailsPetAllowedAndCountWebDto } from '@zg-rentals/ts-rental-manager-api-client';

type PetKey = 'none' | 'catsDto' | 'smallDogsDto' | 'largeDogsDto';

const normalizePetAllowedAndCountDto = (
  dto?: Partial<GetUserPropertyDetailsPetAllowedAndCountWebDto> | null,
): GetUserPropertyDetailsPetAllowedAndCountWebDto => {
  const allowed = dto?.allowed === true;
  return {
    allowed,
    count: allowed ? (dto?.count ?? 1) : 0,
  };
};

const PetPolicyFormField = ({ isRelistingFieldset = false }: { isRelistingFieldset?: boolean }) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { mutateAsync: updatePets } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_pets,
    successToastMessage: 'Pet policy updated',
    meta: { context: { api: 'RentalManagerUserPropertyEditWebApiHandler_pets', details: { listingAlias } } },
  });

  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const rawPetsAllowed = listingDetails?.petsAllowed;

  // Unchecking a pet type saves no count and the follow-up listing-details refetch re-seeds the form with count
  // 0, so remember the last known count in a ref (which the re-seed can't wipe) to restore it on a re-check.
  const lastKnownCountRef = useRef<Partial<Record<PetKey, number>>>({});
  const petsAllowed = {
    ...rawPetsAllowed,
    catsDto: normalizePetAllowedAndCountDto(rawPetsAllowed?.catsDto),
    smallDogsDto: normalizePetAllowedAndCountDto(rawPetsAllowed?.smallDogsDto),
    largeDogsDto: normalizePetAllowedAndCountDto(rawPetsAllowed?.largeDogsDto),
    none: propertyUtils.getIsPetsAllowedEmpty(rawPetsAllowed),
  };
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [errorMessage, setErrorMessage] = useState('');

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
    resetForm,
  } = useForm(
    {
      petsAllowed,
    },
    postingConstraints,
  );

  // useForm only resyncs on a primitive initial-value key, which never changes for this object form, so re-seed
  // it when the listing's pet values change (e.g. after a feed override is turned off) instead of showing stale
  // overrides.
  useEffect(() => {
    resetForm();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when the listing's pet values change
  }, [rawPetsAllowed]);

  // Clear the remembered counts when the route switches listings (this subtree isn't remounted on an in-place
  // switch), so listing A's count can't leak onto listing B. Keyed on listingAlias, not the refetched values,
  // so it survives the re-seed within a listing.
  useEffect(() => {
    lastKnownCountRef.current = {};
  }, [listingAlias]);

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.POLICIES);

  const getUpdatedPetsAllowed = (key: PetKey) => {
    const newPetsAllowed = { ...inputs?.petsAllowed };

    // Clicking any pet type should deselect "none"
    if (key !== 'none') {
      newPetsAllowed.none = false;
    }

    switch (key) {
      case 'none':
        // Stash each count before "No pets allowed" zeroes them, so re-checking a type later restores its count.
        (['catsDto', 'smallDogsDto', 'largeDogsDto'] as const).forEach((petKey) => {
          const petCount = inputs?.petsAllowed?.[petKey]?.count;
          if (petCount) {
            lastKnownCountRef.current[petKey] = petCount;
          }
        });
        newPetsAllowed.catsDto = normalizePetAllowedAndCountDto({ allowed: false });
        newPetsAllowed.smallDogsDto = normalizePetAllowedAndCountDto({ allowed: false });
        newPetsAllowed.largeDogsDto = normalizePetAllowedAndCountDto({ allowed: false });
        newPetsAllowed.none = true;
        break;

      case 'catsDto':
      case 'smallDogsDto':
      case 'largeDogsDto': {
        const currentDto = inputs?.petsAllowed?.[key];
        const willBeAllowed = !(currentDto?.allowed === true);
        // Stash the count before disabling wipes it, and restore it (or the last known value) on re-enable so a
        // re-check republishes the feed's count instead of 1. Never post count 0 for an allowed pet.
        if (!willBeAllowed && currentDto?.count) {
          lastKnownCountRef.current[key] = currentDto.count;
        }
        newPetsAllowed[key] = {
          allowed: willBeAllowed,
          count: lastKnownCountRef.current[key] || currentDto?.count || 1,
        };
        break;
      }

      default:
        break;
    }

    // If no pets are selected, automatically select "none"
    const hasAnyPet =
      newPetsAllowed.catsDto?.allowed || newPetsAllowed.smallDogsDto?.allowed || newPetsAllowed.largeDogsDto?.allowed;
    if (!hasAnyPet) {
      newPetsAllowed.none = true;
    }

    return newPetsAllowed;
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const newPetsAllowed = getUpdatedPetsAllowed(name as PetKey);

    // field error check needs to be above must click on cta check
    if (hasFieldError(name, value)) {
      setErrorMessage('Enter your pet policy');
      return;
    }

    const mockEvent = {
      target: {
        name: 'petsAllowed',
        value: newPetsAllowed,
      },
    };
    handleFormChange(mockEvent);

    try {
      // The form state keeps the count for a just-disabled type so a re-check restores it, but the saved payload
      // must stay normalized (no count for disallowed pets), so normalize here before sending.
      await updatePets({
        postData: {
          listingAlias,
          cats: normalizePetAllowedAndCountDto(newPetsAllowed.catsDto),
          smallDogs: normalizePetAllowedAndCountDto(newPetsAllowed.smallDogsDto),
          largeDogs: normalizePetAllowedAndCountDto(newPetsAllowed.largeDogsDto),
        },
      });
    } catch (err) {
      const errorMessage = DatarouterError.getDisplayMessage(err, 'Pet policy couldn’t be saved. Try again.');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  const fieldSetLabel = isRelistingFieldset ? 'What’s your pet policy?' : 'Pets';

  return (
    <div data-testid="PetPolicyFormField">
      <FieldSet
        disabled={isInputFormDisabled}
        required={!isMultifamily && !isInputFormDisabled}
        legend={<Legend size={isRelistingFieldset ? 'lg' : 'md'}>{isMultifamily ? 'Allowed' : fieldSetLabel}</Legend>}
        error={!!errorMessage}
        description={<FormHelp>{errorMessage}</FormHelp>}
      >
        <LabeledControl
          label={<Label>No pets allowed</Label>}
          control={<Checkbox name="none" checked={inputs?.petsAllowed?.none === true} onChange={handleChange} />}
        />
        <LabeledControl
          label={<Label>Cats allowed</Label>}
          control={
            <Checkbox name="catsDto" checked={inputs?.petsAllowed?.catsDto?.allowed === true} onChange={handleChange} />
          }
        />
        <LabeledControl
          label={<Label>Small dogs allowed</Label>}
          control={
            <Checkbox
              name="smallDogsDto"
              checked={inputs?.petsAllowed?.smallDogsDto?.allowed === true}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          label={<Label>Large dogs allowed</Label>}
          control={
            <Checkbox
              name="largeDogsDto"
              checked={inputs?.petsAllowed?.largeDogsDto?.allowed === true}
              onChange={handleChange}
            />
          }
        />
      </FieldSet>
    </div>
  );
};

export default memo(PetPolicyFormField);
